import { InventoryBoundary } from '@cibo/core'
import { GridColumns, GridEditRowsModel } from '@mui/x-data-grid-pro'
import { DataGridPro } from 'consts/Theme'
import { omit, propEq } from 'ramda'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface BasePeriodRow {
  year: number
  soy?: number
  corn?: number
}

type HistoricalOfftakeTableProps = {
  boundary: InventoryBoundary
  years: number[]
  onChange(change: any): void
}

export const HistoricalOfftakeTable = ({
  boundary,
  years,
  onChange,
}: HistoricalOfftakeTableProps) => {
  const { t } = useTranslation('impact/Inventories/HistoricalOfftake')
  const { t: cropT } = useTranslation('@cibo/ui/cropNames')

  const columns = useMemo(() => {
    return [
      {
        field: 'year',
        headerName: t('year'),
        sortable: false,
        width: 70,
      },
      ...boundary.crops.map(({ crop }) => ({
        field: crop,
        sortable: false,
        headerName: t('cropOfftake', { crop: cropT(crop) }),
        editable: true,
        type: 'number',
        flex: 1,
      })),
    ]
  }, [boundary])

  const [rows, setRows] = useState<BasePeriodRow[]>([])

  useEffect(() => {
    const newRows: BasePeriodRow[] = years.map(year => {
      const offtakes = boundary.data?.cropOfftakes?.find(propEq('year', year))

      return {
        year,
        ...offtakes?.kgOfftakeByCrops,
      }
    })
    setRows(newRows)
  }, [boundary])

  const handleRowsModelChanged = (change: GridEditRowsModel) => {
    const newRows: BasePeriodRow[] = []

    rows.forEach(row => {
      if (change[row.year]) {
        for (const [crop, { value }] of Object.entries(change[row.year])) {
          row[crop as keyof BasePeriodRow] = value
        }
      }

      newRows.push(row)
    })

    setRows(newRows)

    const offtakes = newRows.map(row => ({
      year: row.year,
      kgOfftakeByCrops: omit(['year'], row),
    }))
    onChange(offtakes)
  }

  return (
    <DataGridPro<BasePeriodRow>
      disableColumnResize
      disableColumnMenu
      hideFooter
      autoHeight
      rows={rows}
      getRowId={row => row.year}
      columns={columns as GridColumns<BasePeriodRow>}
      density="compact"
      onEditRowsModelChange={handleRowsModelChanged}
    />
  )
}
